import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "react-toastify";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowRight,
  faCalendarAlt,
  faCheckCircle,
  faChevronLeft,
  faChevronRight,
  faCoins,
  faEdit,
  faGift,
  faHourglassHalf,
  faPlus,
  faUsers,
} from "@fortawesome/free-solid-svg-icons";
import { Card, Col, Row, Form, Button, Modal, Badge, Table } from "@themesberg/react-bootstrap";
import {
  adminCreateBonusCycle,
  adminGetBonusCycleById,
  adminGetBonusCycles,
  adminGetBonusDashboard,
  adminUpdateBonusCycle,
} from "../../../pages/api";
import { Routes } from "../../../routes";
import { Link } from "react-router-dom";

const ACTIVE_BUTTON_STYLE = {
  background: "#1D4E89",
  border: "none",
  borderRadius: "0.5rem",
  color: "#ffffff",
};

const getAdminBonusSlabPath = () => {
  return Routes?.AdminBonusSlabSetup?.path || "/admin-bonus-slab-setup";
};

const STATUS_OPTIONS = ["Draft", "Ready to Process", "Processed", "Archived"];

const badgeVariant = (status) => {
  const s = String(status || "").toLowerCase();
  if (["active", "processed", "sent", "eligible", "ready to process", "ready"].includes(s)) return "success";
  if (["pending", "draft"].includes(s)) return "warning";
  if (["inactive", "failed", "not eligible", "already claimed", "archived"].includes(s)) return "danger";
  return "secondary";
};

const formatNumber = (value) => Number(value || 0).toLocaleString("en-IN");

const formatDateTime = (value) => {
  if (!value) return "N/A" || "-";

  const normalized = String(value).replace(" ", "T");
  const date = new Date(normalized);

  if (Number.isNaN(date.getTime())) {
    return String(value);
  }

  return date.toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const formatMonthLabel = (value) => {
  if (!value) return "N/A" || "-";
  const [year, month] = String(value).split("-");
  if (!year || !month) return String(value);

  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return String(value);

  return date.toLocaleString("en-IN", {
    month: "long",
    year: "numeric",
  });
};

const mapCycle = (item) => ({
  id: item?.id ?? Date.now(),
  cycle_name: item?.cycle_name || "",
  cycle_month: item?.cycle_month || "",
  status: item?.status || "Draft",
  notes: item?.notes || "",
  created_at: item?.created_at || "",
});

export default function AdminBonusDashboard() {
  const monthInputRef = useRef(null);

  const [dashboardData, setDashboardData] = useState({
    current_cycle: null,
    total_eligible_users: 0,
    total_processed_gifts: 0,
    total_pending_gifts: 0,
    total_failed_gifts: 0,
    total_bonus_amount: 0,
    slab_wise_counts: [],
  });

  const [cycles, setCycles] = useState([]);
  const [selectedCycleId, setSelectedCycleId] = useState(null);

  const [loadingDashboard, setLoadingDashboard] = useState(false);
  const [loadingCycles, setLoadingCycles] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [showModal, setShowModal] = useState(false);
  const [editingCycle, setEditingCycle] = useState(null);
  const [formData, setFormData] = useState({
    cycle_name: "",
    cycle_month: "",
    status: "Draft",
    notes: "",
  });
  const [errors, setErrors] = useState({
    cycle_name: "",
    cycle_month: "",
    status: "",
  });

  const [pagination, setPagination] = useState({
    current_page: 1,
    last_page: 1,
    per_page: 10,
    total: 0,
  });

  const openMonthPicker = (e) => {
    const input = e.currentTarget;
    if (typeof input.showPicker === "function") {
      try {
        input.showPicker();
      } catch (error) {
        // native fallback
      }
    }
  };

  const fetchDashboard = async (cycleId = null) => {
    try {
      setLoadingDashboard(true);

      const params = {};
      if (cycleId) {
        params.cycle_id = cycleId;
      }

      const response = await adminGetBonusDashboard(params);
      const responseData = response?.data || {};
      const apiData = responseData?.data || {};

      setDashboardData({
        current_cycle: apiData?.current_cycle || null,
        total_eligible_users: apiData?.total_eligible_users ?? 0,
        total_processed_gifts: apiData?.total_processed_gifts ?? 0,
        total_pending_gifts: apiData?.total_pending_gifts ?? 0,
        total_failed_gifts: apiData?.total_failed_gifts ?? 0,
        total_bonus_amount: apiData?.total_bonus_amount ?? 0,
        slab_wise_counts: Array.isArray(apiData?.slab_wise_counts) ? apiData.slab_wise_counts : [],
      });

      if (apiData?.current_cycle?.id) {
        setSelectedCycleId(apiData.current_cycle.id);
      }
    } catch (error) {
      console.error("Failed to fetch bonus dashboard:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch dashboard data.");
      setDashboardData({
        current_cycle: null,
        total_eligible_users: 0,
        total_processed_gifts: 0,
        total_pending_gifts: 0,
        total_failed_gifts: 0,
        total_bonus_amount: 0,
        slab_wise_counts: [],
      });
    } finally {
      setLoadingDashboard(false);
    }
  };

  const fetchCycles = async (page = 1, perPage = 10) => {
    try {
      setLoadingCycles(true);

      const response = await adminGetBonusCycles({
        page,
        per_page: perPage,
      });

      const responseData = response?.data || {};
      const apiData = responseData?.data || {};
      const apiCycles = Array.isArray(apiData?.cycles) ? apiData.cycles : [];
      const apiPagination = apiData?.pagination || {};

      setCycles(apiCycles.length > 0 ? apiCycles.map(mapCycle) : []);
      setPagination({
        current_page: apiPagination.current_page ?? 1,
        last_page: apiPagination.last_page ?? 1,
        per_page: apiPagination.per_page ?? 10,
        total: apiPagination.total ?? apiCycles.length,
      });
    } catch (error) {
      console.error("Failed to fetch bonus cycles:", error);
      toast.error(error?.response?.data?.message || "Unable to fetch bonus cycles.");
      setCycles([]);
      setPagination({
        current_page: 1,
        last_page: 1,
        per_page: 10,
        total: 0,
      });
    } finally {
      setLoadingCycles(false);
    }
  };

  useEffect(() => {
    (async () => {
      await Promise.all([fetchCycles(1, 10), fetchDashboard()]);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const slabCards = useMemo(() => {
    const slabWiseCounts = Array.isArray(dashboardData?.slab_wise_counts) ? dashboardData.slab_wise_counts : [];

    return slabWiseCounts.map((item, index) => ({
      id: item?.slab_id ?? index + 1,
      name: `Slab-${item?.slab_id ?? index + 1}`,
      threshold: item?.threshold ?? 0,
      count: item?.count ?? 0,
      // gift_name: item?.gift_name || "",
    }));
  }, [dashboardData?.slab_wise_counts]);

  const getShowingRange = () => {
    const total = Number(pagination.total || 0);
    const current = Number(pagination.current_page || 1);
    const perPage = Number(pagination.per_page || 10);
    const currentRows = Array.isArray(cycles) ? cycles.length : 0;

    if (total === 0 || currentRows === 0) {
      return { start: 0, end: 0 };
    }

    let start = (current - 1) * perPage + 1;
    let end = Math.min(start + currentRows - 1, total);

    if (start > total) {
      start = total;
      end = total;
    }

    if (end < start) {
      end = start;
    }

    return { start, end };
  };

  const getPageItems = () => {
    const totalPages = pagination.last_page || 1;
    const current = pagination.current_page || 1;

    if (totalPages <= 5) {
      return Array.from({ length: totalPages }, (_, i) => i + 1);
    }

    if (current <= 2) {
      return [1, 2, 3, "...", totalPages];
    }

    if (current >= totalPages - 1) {
      return [1, "...", totalPages - 2, totalPages - 1, totalPages];
    }

    return [1, "...", current - 1, current, current + 1, "...", totalPages];
  };

  const { start, end } = useMemo(() => getShowingRange(), [pagination, cycles]);

  const openAddModal = () => {
    setEditingCycle(null);
    setFormData({
      cycle_name: "",
      cycle_month: "",
      status: "Draft",
      notes: "",
    });
    setErrors({
      cycle_name: "",
      cycle_month: "",
      status: "",
    });
    setShowModal(true);
  };

  const openEditModal = async (cycle) => {
    setShowModal(true);

    try {
      const response = await adminGetBonusCycleById(cycle.id);
      const apiCycle = response?.data?.data || cycle;

      setEditingCycle(apiCycle);
      setFormData({
        cycle_name: apiCycle?.cycle_name || "",
        cycle_month: apiCycle?.cycle_month || "",
        status: apiCycle?.status || "Draft",
        notes: apiCycle?.notes || "",
      });
    } catch (error) {
      console.error("Failed to fetch cycle by id:", error);
      setEditingCycle(cycle);
      setFormData({
        cycle_name: cycle?.cycle_name || "",
        cycle_month: cycle?.cycle_month || "",
        status: cycle?.status || "Draft",
        notes: cycle?.notes || "",
      });
    }

    setErrors({
      cycle_name: "",
      cycle_month: "",
      status: "",
    });
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingCycle(null);
    setFormData({
      cycle_name: "",
      cycle_month: "",
      status: "Draft",
      notes: "",
    });
    setErrors({
      cycle_name: "",
      cycle_month: "",
      status: "",
    });
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    if (name === "cycle_name" || name === "cycle_month" || name === "status") {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const cycleName = String(formData.cycle_name || "").trim();
    const cycleMonth = String(formData.cycle_month || "").trim();
    const status = String(formData.status || "").trim();
    const notes = String(formData.notes || "").trim();

    const newErrors = {
      cycle_name: !cycleName ? "Cycle Name is required" : "",
      cycle_month: !cycleMonth ? "Cycle Month is required" : "",
      status: !status ? "Status is required" : "",
    };

    setErrors(newErrors);

    if (newErrors.cycle_name || newErrors.cycle_month || newErrors.status) {
      toast.error("Please fill all required fields.");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        cycle_name: cycleName,
        cycle_month: cycleMonth,
        status,
        notes,
      };

      const response = editingCycle
        ? await adminUpdateBonusCycle(editingCycle.id, payload)
        : await adminCreateBonusCycle(payload);

      toast.success(response?.data?.message || `Bonus cycle ${editingCycle ? "updated" : "created"} successfully.`);
      closeModal();
      await Promise.all([fetchCycles(pagination.current_page, pagination.per_page), fetchDashboard(selectedCycleId)]);
    } catch (error) {
      console.error("Bonus cycle save failed:", error);
      toast.error(error?.response?.data?.message || "Unable to save bonus cycle.");
    } finally {
      setSubmitting(false);
    }
  };

  const handlePageChange = async (page) => {
    if (page === pagination.current_page || loadingCycles) return;
    await fetchCycles(page, pagination.per_page);
  };

  const handleCycleView = async (cycleId) => {
    setSelectedCycleId(cycleId);
    await fetchDashboard(cycleId);
  };

  return (
    <>
      <div className="d-flex justify-content-between flex-wrap flex-md-nowrap align-items-center py-4 gap-2">
        <div>
          <h2 className="h4 mb-1">Bonus Management</h2>
          {/* <div className="text-muted">Dashboard, slab eligibility, and bonus cycles management.</div> */}
        </div>

        <div className="d-flex justify-content-end ms-auto">
          <Button
            as={Link}
            to={getAdminBonusSlabPath()}
            style={ACTIVE_BUTTON_STYLE}
          >
            <FontAwesomeIcon icon={faArrowRight} className="me-2" />
            Bonus Slab
          </Button>
        </div>

        {/* <Button type="button" onClick={openAddModal} style={ACTIVE_BUTTON_STYLE} className="fw-semibold">
          <FontAwesomeIcon icon={faPlus} className="me-2" />
          Add Cycle
        </Button> */}
      </div>

      <Row className="g-4 mb-4">
        <Col xs={12} sm={6} xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Current Cycle</div>
                  <h3 className="mb-0">
                    {loadingDashboard ? "Loading..." : dashboardData?.current_cycle?.cycle_name || "N/A" || "-"}
                  </h3>
                  <div className="text-muted small mt-1">
                    {dashboardData?.current_cycle?.cycle_month ? formatMonthLabel(dashboardData.current_cycle.cycle_month) : ""}
                  </div>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                >
                  <FontAwesomeIcon icon={faCalendarAlt} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

         <Col xs={12} sm={6} xl={6}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Bonus Amount</div>
                  <h3 className="mb-0">
                    {loadingDashboard
                      ? "..."
                      : `${formatNumber(dashboardData.total_bonus_amount)}`}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                >
                  <FontAwesomeIcon icon={faCoins} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Eligible Users</div>
                  <h3 className="mb-0">{loadingDashboard ? "..." : formatNumber(dashboardData.total_eligible_users)}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                >
                  <FontAwesomeIcon icon={faUsers} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Processed Gifts</div>
                  <h3 className="mb-0">{loadingDashboard ? "..." : formatNumber(dashboardData.total_processed_gifts)}</h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                >
                  <FontAwesomeIcon icon={faCheckCircle} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} sm={6} xl={4}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-start">
                <div>
                  <div className="text-muted small mb-1">Total Pending / Failed</div>
                  <h3 className="mb-0">
                    {loadingDashboard
                      ? "..."
                      : `${formatNumber(dashboardData.total_pending_gifts)} / ${formatNumber(dashboardData.total_failed_gifts)}`}
                  </h3>
                </div>
                <div
                  className="rounded-circle d-flex align-items-center justify-content-center"
                  style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                >
                  <FontAwesomeIcon icon={faHourglassHalf} />
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>

        
      </Row>

      <Row className="g-4 mb-4">
        {slabCards.length > 0 ? (
          slabCards.map((slab) => (
            <Col key={slab.id} xs={12} sm={6} xl={4}>
              <Card className="border-0 shadow-sm rounded-4 h-100">
                <Card.Body className="p-4">
                  <div className="d-flex justify-content-between align-items-start">
                    <div>
                      <div className="text-muted small mb-1">{slab.name} Eligible Count</div>
                      <h3 className="mb-0">{formatNumber(slab.count)}</h3>
                      <div className="text-muted small">
                        Threshold: {formatNumber(slab.threshold)}
                        {/* {slab.gift_name ? ` • ${slab.gift_name}` : ""} */}
                      </div>
                    </div>
                    <div
                      className="rounded-circle d-flex align-items-center justify-content-center"
                      style={{ width: 44, height: 44, background: "rgba(44, 96, 186, 0.1)", color: "#005dfb" }}
                    >
                      <FontAwesomeIcon icon={faGift} />
                    </div>
                  </div>
                </Card.Body>
              </Card>
            </Col>
          ))
        ) : (
          <Col xs={12}>
            <Card className="border-0 shadow-sm rounded-4">
              <Card.Body className="p-4 text-muted">No slab-wise count data found.</Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      <Row className="g-4">
        <Col xs={12} md={12}>
          <Card className="border-0 shadow-sm rounded-4 h-100">
            <Card.Body className="p-4">
              <div className="d-flex justify-content-between align-items-center mb-3 flex-wrap gap-2">
                <div>
                  <h5 className="mb-1">Cycle List</h5>
                  {/* <div className="text-muted small">View and manage all bonus cycles.</div> */}
                </div>

                <div className="d-flex justify-content-end ms-auto">
                  <Button type="button" onClick={openAddModal} style={ACTIVE_BUTTON_STYLE} className="fw-semibold">
                    <FontAwesomeIcon icon={faPlus} className="me-2" />
                    Add Cycle
                  </Button>
                </div>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0">
                  <thead>
                    <tr>
                      <th>Sr No</th>
                      <th>Cycle Name</th>
                      <th>Cycle Month</th>
                      <th>Status</th>
                      <th>Notes</th>
                      {/* <th>Created At</th> */}
                      <th className="text-center">Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingCycles ? (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          Loading bonus cycles...
                        </td>
                      </tr>
                    ) : cycles.length > 0 ? (
                      cycles.map((item, index) => (
                        <tr key={item.id}>
                          <td className="fw-semibold">{start + index}</td>
                          <td>{item.cycle_name}</td>
                          <td>{formatMonthLabel(item.cycle_month)}</td>
                          <td>
                            <Badge bg={badgeVariant(item.status)} className="rounded-pill px-3 py-2">
                              {item.status}
                            </Badge>
                          </td>
                          <td>{item.notes || "N/A" || "-"}</td>
                          {/* <td>{formatDateTime(item.created_at)}</td> */}
                          <td className="text-center">
                            <Button
                              type="button"
                              variant="link"
                              className="p-0 text-decoration-none"
                              onClick={() => openEditModal(item)}
                              title="Edit Cycle"
                            >
                              <FontAwesomeIcon icon={faEdit} size="sm" />
                            </Button>
                          </td>
                        </tr>
                      ))
                    ) : (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-5">
                          No bonus cycles found.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </Table>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                <div className="text-muted small">
                  Showing {start} to {end} of {pagination.total} entries
                </div>

                <div className="d-flex align-items-center gap-2 flex-wrap">
                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={pagination.current_page <= 1 || loadingCycles}
                    onClick={() => handlePageChange(Math.max(1, pagination.current_page - 1))}
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronLeft} />
                  </Button>

                  {getPageItems().map((item, index) =>
                    item === "..." ? (
                      <span key={`dots-${index}`} className="text-muted px-1">
                        ...
                      </span>
                    ) : (
                      <Button
                        key={item}
                        type="button"
                        variant="outline-primary"
                        disabled={loadingCycles}
                        onClick={() => handlePageChange(item)}
                        className={`course-page-btn ${item === pagination.current_page ? "course-page-btn-active" : ""}`}
                      >
                        {item}
                      </Button>
                    )
                  )}

                  <Button
                    type="button"
                    variant="outline-primary"
                    disabled={pagination.current_page >= pagination.last_page || loadingCycles}
                    onClick={() => handlePageChange(Math.min(pagination.last_page, pagination.current_page + 1))}
                    className="course-page-btn"
                  >
                    <FontAwesomeIcon icon={faChevronRight} />
                  </Button>
                </div>
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      <Modal show={showModal} onHide={closeModal} centered dialogClassName="bonus-cycle-modal">
        <Form onSubmit={handleSubmit}>
          <Modal.Header closeButton>
            <Modal.Title>{editingCycle ? "Edit Cycle" : "Add Cycle"}</Modal.Title>
          </Modal.Header>

          <Modal.Body>
            <Form.Group className="mb-3" controlId="cycleName">
              <Form.Label className="fw-semibold">
                Cycle Name <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                name="cycle_name"
                type="text"
                placeholder="Enter cycle name"
                value={formData.cycle_name}
                onChange={handleChange}
                style={{ boxShadow: "none" }}
                isInvalid={!!errors.cycle_name}
              />
              <Form.Control.Feedback type="invalid">{errors.cycle_name}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="cycleMonth">
              <Form.Label className="fw-semibold">
                Month / Year <span className="text-danger">*</span>
              </Form.Label>
              <Form.Control
                ref={monthInputRef}
                name="cycle_month"
                type="month"
                value={formData.cycle_month}
                onChange={handleChange}
                onMouseDown={openMonthPicker}
                onClick={openMonthPicker}
                className="network-month-filter"
                style={{ boxShadow: "none" }}
                isInvalid={!!errors.cycle_month}
              />
              <Form.Control.Feedback type="invalid">{errors.cycle_month}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-3" controlId="cycleStatus">
              <Form.Label className="fw-semibold">
                Status <span className="text-danger">*</span>
              </Form.Label>
              <Form.Select
                name="status"
                value={formData.status}
                onChange={handleChange}
                style={{ boxShadow: "none" }}
                isInvalid={!!errors.status}
              >
                {STATUS_OPTIONS.map((item) => (
                  <option key={item} value={item}>
                    {item}
                  </option>
                ))}
              </Form.Select>
              <Form.Control.Feedback type="invalid">{errors.status}</Form.Control.Feedback>
            </Form.Group>

            <Form.Group className="mb-0" controlId="cycleNotes">
              <Form.Label className="fw-semibold">Notes</Form.Label>
              <Form.Control
                name="notes"
                as="textarea"
                rows={4}
                placeholder="Enter notes"
                value={formData.notes}
                onChange={handleChange}
                style={{ boxShadow: "none" }}
              />
            </Form.Group>
          </Modal.Body>

          <Modal.Footer>
            <Button onClick={closeModal} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" style={ACTIVE_BUTTON_STYLE} disabled={submitting}>
              {submitting ? "Saving..." : editingCycle ? "Update Cycle" : "Add Cycle"}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>

      <style>{`
        .modal-dialog.bonus-cycle-modal {
         width: min(95vw, 70rem) !important;
  max-width: 50rem !important;
  margin: 1.75rem auto !important;
        }

        @media (max-width: 767.98px) {
          .modal-dialog.bonus-cycle-modal {
            width: min(96vw, 36rem) !important;
            max-width: 36rem !important;
            margin: 1rem auto !important;
          }
        }
      `}</style>
    </>
  );
}